import React from "react";
import { NavLink } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { pickLocale } from "../../lib/format";

/**
 * The primary row.
 *
 * The disclosure is a separate control from the link, for three reasons that
 * all matter: `aria-expanded` does not belong on a link, `NavLink` keeps its
 * own active styling, and on a touch device the label still navigates on the
 * first tap while the chevron opens the panel — no dead first tap.
 *
 * Every entry that has anything to show opens the one full-width panel. There
 * is deliberately no second, smaller dropdown style: two kinds of menu under
 * one row is how a navigation starts looking assembled rather than designed.
 */
export default function DesktopNavigation({
  items, megaKey, panelId, quiet,
  hasPanel, hoverCapable, activeKey,
  onOpen, onScheduleClose, onCancelClose, onToggle,
  registerTrigger,
}) {
  const { locale, t } = useI18n();

  return (
    <nav
      className={`adf-primary-nav${quiet ? " opacity-75" : ""}`}
      aria-label="Primary"
      data-testid="primary-nav"
    >
      {items.map((item) => {
        const key = item.key || item.to;
        const panel = hasPanel(item);
        const open = megaKey === key;
        const label = pickLocale(item.title, locale);
        const badge = pickLocale(item.badge, locale);
        // OUTLET həmişə qalın və qırmızıdır (tapşırıq 2026-10-05) — admin
        // vurğu seçməsə də, bəndin ünvanı bunu müəyyən edir.
        const accent = item.accent || (item.to === "/outlet" ? "outlet" : undefined);
        return (
          <div
            key={key}
            className="adf-nav-item"
            data-open={open ? "true" : "false"}
            onMouseEnter={panel && hoverCapable ? () => onOpen(key) : undefined}
            onMouseLeave={panel && hoverCapable ? onScheduleClose : undefined}
          >
            <NavLink
              to={item.to || "#"}
              /* NavLink owns `aria-current` — it writes its own and drops any
                 passed in, so the section-level cue is carried by the class
                 alone; the exact page still gets NavLink's own announcement. */
              className={({ isActive }) => (
                `adf-nav-link${(isActive && item.to) || key === activeKey ? " is-active" : ""}`
              )}
              data-accent={accent}
            >
              {label}
              {badge && <span className="adf-nav-badge">{badge}</span>}
            </NavLink>

            {panel && (
              <button
                type="button"
                ref={(node) => registerTrigger(key, node)}
                className="adf-nav-disclosure"
                aria-expanded={open}
                aria-haspopup="true"
                aria-controls={panelId}
                aria-label={`${label} — ${t("nav.open", "Aç")}`}
                data-testid="mega-menu-trigger"
                onFocus={hoverCapable ? onCancelClose : undefined}
                onClick={() => onToggle(key)}
                onKeyDown={(event) => {
                  if (event.key !== "ArrowDown") return;
                  event.preventDefault();
                  onOpen(key, { focusPanel: true });
                }}
              >
                <ChevronDown size={13} strokeWidth={1.5} aria-hidden="true" />
              </button>
            )}
          </div>
        );
      })}
    </nav>
  );
}
